import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';

export interface PrecheckResponse {
	ok: boolean;
	checks: {
		assigned: boolean;
		published: boolean;
		withinWindow: boolean;
		windowOpensAt: string;
		windowClosesAt: string;
		canOverrideEarly: boolean;
		faceEnrolled: boolean;
		pinSet: boolean;
	};
	blockers: string[];
	suggestedNext: {
		source: 'continuation' | 'plan' | 'none';
		noneReason?: 'never_assigned' | 'all_archived' | 'all_completed';
		curriculumId?: string;
		curriculumTitle?: string;
		moduleIndex?: number;
		sessionIndex?: number;
		moduleTitle?: string;
		sessionTitle?: string;
		planStatus?: string;
		continuationFromSlot?: string;
	};
}

export interface AttendanceRosterEntry {
	student: {
		id: string;
		fullName: string;
		rollNumber: string;
		alforaaId: string | null;
	};
	startMark: 'present' | 'absent' | 'present_no_device' | 'late';
	source: 'auto_device' | 'manual' | 'late_add';
	deviceActive: boolean;
}

export interface AttendanceClassGroup {
	slotId: string;
	classLabel: string;
	rosterCount: number;
	entries: AttendanceRosterEntry[];
	confirmedAt: string | null;
}

export interface AttendanceRoster {
	// Per-class roster groups. For a solo period this has one entry (the slot's
	// own class); for a clubbed period it has one per clubbed class so the UI
	// can render a tab each. Present on the primary slot's roster response.
	classes: AttendanceClassGroup[];
	clubbed: boolean;
	rosterCount: number;
	entries: AttendanceRosterEntry[];
	windowMinutes: number;
	confirmedAt: string | null;
}

export interface AttendanceSummary {
	total: number;
	present: number;
	absent: number;
	presentNoDevice: number;
	late: number;
	leftEarly: number;
}

export interface SessionState {
	slot: any;
	log: any;
	attendance: any;
	capture: any;
	captures: any[];
	photoCount: number;
	kit: any;
}

export const trainerSessionFlowApi = createApi({
	reducerPath: 'trainerSessionFlowApi',
	baseQuery: axiosBaseQuery,
	tagTypes: ['Precheck', 'Roster', 'SessionState', 'KitReq', 'ClassPhotos'],
	endpoints: (builder) => ({
		precheck: builder.query<PrecheckResponse, string>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/precheck`,
			}),
			providesTags: (_r, _e, id) => [{ type: 'Precheck', id }],
		}),
		verifyFace: builder.mutation<
			{
				matched: boolean;
				confidence: number | null;
				attempts: number;
				trainerName: string | null;
			},
			{ slotId: string; faceDescriptor: number[]; allowEarly?: boolean }
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/verify-face`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		verifyPin: builder.mutation<
			{ matched: boolean; method: string },
			{ slotId: string; pin: string; allowEarly?: boolean }
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/verify-pin`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		uploadClassroomPhoto: builder.mutation<
			{ ok: boolean; geofenceOk: boolean | null },
			{
				slotId: string;
				photoBase64: string;
				mimeType?: string;
				gps?: { latitude: number; longitude: number };
				gpsAccuracyMeters?: number;
			}
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/classroom-photo`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
				{ type: 'ClassPhotos', id: slotId },
			],
		}),
		listClassroomPhotos: builder.query<
			{ photos: { id: string; photoData: string; mimeType: string; capturedAt: string; geofenceOk: boolean | null }[] },
			string
		>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/classroom-photos`,
			}),
			providesTags: (_r, _e, id) => [{ type: 'ClassPhotos', id }],
		}),
		deleteClassroomPhoto: builder.mutation<
			{ ok: boolean; photoCount: number },
			{ slotId: string; photoId: string }
		>({
			query: ({ slotId, photoId }) => ({
				url: `/trainer/timetable/slots/${slotId}/classroom-photos/${photoId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'ClassPhotos', id: slotId },
				{ type: 'SessionState', id: slotId },
			],
		}),
		attendanceRoster: builder.query<AttendanceRoster, string>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/attendance-roster`,
			}),
			providesTags: (_r, _e, id) => [{ type: 'Roster', id }],
		}),
		confirmAttendance: builder.mutation<
			{
				ok: boolean;
				summary: AttendanceSummary;
				slotStatus: string;
				startedAt: string;
			},
			{
				slotId: string;
				entries: {
					studentId: string;
					startMark: string;
					source?: string;
				}[];
				// Attendance for the other clubbed classes, keyed by their slot id.
				// Each clubbed class is marked individually in the same UI; all
				// classes start together on this single confirm.
				siblingEntries?: Record<
					string,
					{ studentId: string; startMark: string; source?: string }[]
				>;
				curriculumId?: string;
				moduleIndex?: number;
				sessionIndex?: number;
				isContinuationOf?: string;
			}
		>({
			query: ({
				slotId,
				entries,
				siblingEntries,
				curriculumId,
				moduleIndex,
				sessionIndex,
				isContinuationOf,
			}) => ({
				url: `/trainer/timetable/slots/${slotId}/confirm-attendance`,
				method: 'POST',
				data: {
					entries,
					siblingEntries,
					curriculumId,
					moduleIndex,
					sessionIndex,
					isContinuationOf,
				},
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
				{ type: 'Roster', id: slotId },
			],
		}),
		// Mid-session curriculum unlock — the in-session step's unlock card.
		// Unlike confirmAttendance this touches curriculum context only, so it
		// never rewrites the attendance roster.
		unlockSession: builder.mutation<
			{
				ok: boolean;
				moduleIndex: number;
				sessionIndex: number;
				clubbedUnlocked: number;
			},
			{
				slotId: string;
				curriculumId: string;
				moduleIndex: number;
				sessionIndex: number;
				// Set when the chosen session continues one that spilled over from
				// an earlier period.
				isContinuationOf?: string;
			}
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/unlock-session`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		sessionState: builder.query<SessionState, string>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/session-state`,
			}),
			providesTags: (_r, _e, id) => [{ type: 'SessionState', id }],
		}),
		kitRequirement: builder.query<
			{
				kitRequired: boolean;
				kitName: string | null;
				// Admin-mapped catalog for this slot's school+grade. Empty when no
				// mapping exists, in which case kitName above is the only label.
				kitOptions?: { id: string; name: string }[];
				machineOptions?: { id: string; name: string }[];
			},
			string
		>({
			query: (slotId) => ({
				url: `/trainer/timetable/slots/${slotId}/kit-required`,
			}),
			providesTags: (_r, _e, id) => [{ type: 'KitReq', id }],
		}),
		markLateArrival: builder.mutation<
			{ ok: boolean },
			{ slotId: string; studentId: string }
		>({
			query: ({ slotId, studentId }) => ({
				url: `/trainer/timetable/slots/${slotId}/late-arrival`,
				method: 'POST',
				data: { studentId },
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		flagStudent: builder.mutation<
			{ ok: boolean },
			{ slotId: string; studentId: string; reason: string }
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/flag-student`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		appendNote: builder.mutation<
			{ ok: boolean },
			{ slotId: string; note: string }
		>({
			query: ({ slotId, note }) => ({
				url: `/trainer/timetable/slots/${slotId}/note`,
				method: 'POST',
				data: { note },
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		updateEndAttendance: builder.mutation<
			{ ok: boolean; summary: AttendanceSummary },
			{
				slotId: string;
				entries: { studentId: string; endMark: string }[];
			}
		>({
			query: ({ slotId, entries }) => ({
				url: `/trainer/timetable/slots/${slotId}/end-attendance`,
				method: 'POST',
				data: { entries },
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		upsertKit: builder.mutation<
			{
				ok: boolean;
				kitName: string;
				counts: {
					provided: number;
					notProvided: number;
					returned: number;
					total: number;
				};
			},
			{
				slotId: string;
				// Optional: omit to leave the stored label untouched.
				kitName?: string;
				// Optional: omit to leave stored per-student marks untouched. An
				// explicit [] clears them; absent means "don't touch".
				entries?: {
					studentId: string;
					mark: 'provided' | 'not_provided' | 'returned';
					notProvidedReason?: string | null;
					note?: string;
				}[];
				// Session-level answers (§3b). Omitted entirely by callers that
				// don't ask the questions — the server leaves stored values alone.
				kitSession?: boolean;
				takeawayGiven?: boolean;
				takeawayKitId?: string | null;
				handsOnSession?: boolean;
				machineId?: string | null;
			}
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/kit`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		submitSession: builder.mutation<
			{ ok: boolean; slotStatus: string; submittedAt: string },
			{
				slotId: string;
				completionStatus: 'completed' | 'partially_completed' | 'not_completed';
				topicsCovered?: string[];
				topicsSkipped?: string[];
				remarks?: string;
				completedEarly?: boolean;
				completedEarlyReason?: string;
				spilledOver?: boolean;
			}
		>({
			query: ({ slotId, ...body }) => ({
				url: `/trainer/timetable/slots/${slotId}/submit`,
				method: 'POST',
				data: body,
			}),
			invalidatesTags: (_r, _e, { slotId }) => [
				{ type: 'SessionState', id: slotId },
			],
		}),
		setTrainerPin: builder.mutation<{ success: boolean }, { pin: string }>({
			query: (body) => ({
				url: `/trainer/timetable/profile/pin`,
				method: 'POST',
				data: body,
			}),
		}),
		enrollTrainerFace: builder.mutation<
			{ success: boolean; faceSetAt: string },
			{ password: string; faceDescriptor: number[] }
		>({
			query: (body) => ({
				url: `/trainer/timetable/profile/face`,
				method: 'POST',
				data: body,
			}),
		}),
	}),
});

export const {
	usePrecheckQuery,
	useVerifyFaceMutation,
	useVerifyPinMutation,
	useUploadClassroomPhotoMutation,
	useListClassroomPhotosQuery,
	useDeleteClassroomPhotoMutation,
	useAttendanceRosterQuery,
	useConfirmAttendanceMutation,
	useUnlockSessionMutation,
	useSessionStateQuery,
	useKitRequirementQuery,
	useMarkLateArrivalMutation,
	useFlagStudentMutation,
	useAppendNoteMutation,
	useUpdateEndAttendanceMutation,
	useUpsertKitMutation,
	useSubmitSessionMutation,
	useSetTrainerPinMutation,
	useEnrollTrainerFaceMutation,
} = trainerSessionFlowApi;
